import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import { AISearchElementsModel } from '../model/ai-search-elements.model';
import type { ElementsState } from '../type/ai-search.type';

export const AISearchElementsService = Symbol('AISearchElementsService');

export interface AISearchElementsService {
    getModelData: () => ElementsState;
    readonly onStatesChange: Event<ElementsState>;
    readonly onQueryListGet: Event<ElementsState>;

    initElementsState: (state: {
       summary: ElementsState['summary'],
    } ,getElements: () => Promise<Omit<ElementsState, 'isLoading' | 'summary'> >) => Promise<void>;
    handleChangeElementsData: (value: Partial<ElementsState>) => void;
    handleGetQueryList: () => void;
}

@injectable()
export class AISearchElementsServiceImpl implements AISearchElementsService {
    private _onStatesChangeEmitter = new Emitter<ElementsState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    private _onQueryListGetEmitter = new Emitter<ElementsState>();
    readonly onQueryListGet = this._onQueryListGetEmitter.event;

    constructor(
        @inject(AISearchElementsModel)
        private _model: AISearchElementsModel,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData(): ElementsState {
        return this._model.modelData;
    }

    private _setModelData(value: Partial<ElementsState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    public async initElementsState(
        state: {
            summary: ElementsState['summary'],
        },
        getElements: () => Promise<Omit<ElementsState, 'isLoading' | 'summary'>>,
    ) {
        this._setModelData({
            isLoading : true,
            summary: state.summary,
        });
        const result = await getElements();
        this._setModelData({
            ...result,
            isLoading: false,
        });
    }

    public handleChangeElementsData(value: Partial<ElementsState>) {
        this._setModelData(value);
    }

    public handleGetQueryList() {
        const state = this.getModelData();
        this._onQueryListGetEmitter.fire(state);
    }
}

